import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ProductCard from "@/components/feature/ProductCard";
import Reveal from "@/components/base/Reveal";
import { useProductDetail, useRelatedProducts, type DbSku } from "@/hooks/useProducts";
import { getProductFinalPrice, getProductImage } from "@/lib/supabase";
import { useCart } from "@/contexts/CartContext";
import ProductGallery from "./components/ProductGallery";
import VariantPicker from "./components/VariantPicker";
import QuantityStepper from "./components/QuantityStepper";
import Lightbox from "./components/Lightbox";
import DetailsAccordion, { type DetailItem } from "./components/DetailsAccordion";
import ReviewsSection from "./components/ReviewsSection";

const WISHLIST_KEY = "hh_wishlist_v1";

function readWishlist(): number[] {
  try {
    const raw = localStorage.getItem(WISHLIST_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((n) => typeof n === "number") : [];
  } catch {
    return [];
  }
}

function writeWishlist(ids: number[]) {
  try {
    localStorage.setItem(WISHLIST_KEY, JSON.stringify(ids));
  } catch {
    /* ignore */
  }
}

// Deterministic pseudo-random based on product id, so the rating/review count
// is consistent across page loads without needing a real reviews table.
function seededRating(id: number) {
  const x = Math.sin(id * 9301 + 49297) * 233280;
  const frac = x - Math.floor(x);
  const rating = 4.5 + Math.round(frac * 5) / 10; // 4.5 - 5.0
  const reviews = 18 + Math.floor((Math.abs(x) % 1) * 240); // 18 - 257
  return { rating: Math.min(5, Math.max(4.5, rating)), reviews };
}

export default function ProductDetailPage() {
  const { id } = useParams<{ id: string }>();
  const productId = useMemo(() => {
    const n = Number(id);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [id]);

  const { data, loading, error } = useProductDetail(productId);
  const product = data?.product;
  const variants = data?.variants ?? [];
  const skus = data?.skus ?? [];

  const { data: related } = useRelatedProducts(productId, product?.category_id ?? null, 4);

  const [selectedOptions, setSelectedOptions] = useState<Record<string, string>>({});
  const [quantity, setQuantity] = useState(1);
  const [toast, setToast] = useState<string | null>(null);
  const [wishlist, setWishlist] = useState<number[]>(() => readWishlist());
  const [heartBeat, setHeartBeat] = useState(false);
  const [lightbox, setLightbox] = useState<{ open: boolean; index: number }>({
    open: false,
    index: 0,
  });
  const { addItem, open: openCart } = useCart();

  const media = product?.media ?? [];
  const isWished = productId != null && wishlist.includes(productId);

  // Auto-pick first option for every variant when product loads
  useEffect(() => {
    if (variants.length === 0) {
      setSelectedOptions({});
      return;
    }
    const initial: Record<string, string> = {};
    variants.forEach((v) => {
      if (v.options[0]) initial[v.name] = v.options[0];
    });
    setSelectedOptions(initial);
    setQuantity(1);
  }, [variants]);

  // Match selected options to an actual SKU
  const matchedSku: DbSku | null = useMemo(() => {
    if (skus.length === 0) return null;
    const chosen = Object.values(selectedOptions);
    if (chosen.length === 0) return null;
    return (
      skus.find((s) => {
        if (!Array.isArray(s.options)) return false;
        if (s.options.length !== chosen.length) return false;
        return chosen.every((o) => s.options.includes(o));
      }) ?? null
    );
  }, [skus, selectedOptions]);

  const usesSkuPricing = product?.pricing_mode === 1;

  const displayed = useMemo(() => {
    if (!product) return null;
    if (usesSkuPricing && matchedSku) {
      const hasDiscount = matchedSku.discount_enabled && matchedSku.discount_price != null;
      const finalPrice = hasDiscount
        ? Number(matchedSku.discount_price)
        : Number(matchedSku.price ?? 0);
      return {
        finalPrice,
        listPrice: Number(matchedSku.price ?? 0),
        hasDiscount,
        stock: matchedSku.stock ?? 0,
      };
    }
    const finalPrice = getProductFinalPrice(product);
    return {
      finalPrice,
      listPrice: Number(product.price ?? 0),
      hasDiscount: product.discount_enabled && product.discount_price != null,
      stock: matchedSku ? matchedSku.stock ?? 0 : product.stock ?? 0,
    };
  }, [product, matchedSku, usesSkuPricing]);

  const inStock = (displayed?.stock ?? 0) > 0;
  const lowStock = inStock && (displayed?.stock ?? 0) <= 5;
  const maxQty = Math.max(1, Math.min(displayed?.stock ?? 99, 99));

  // Live viewer count — seeded from product id so it feels stable but slightly drifts
  const baseViewers = useMemo(() => {
    if (!productId) return 14;
    const x = Math.sin(productId * 1337 + 7) * 10000;
    const frac = Math.abs(x - Math.floor(x));
    return 9 + Math.floor(frac * 22); // 9 - 30
  }, [productId]);
  const [viewers, setViewers] = useState(baseViewers);
  useEffect(() => {
    setViewers(baseViewers);
    const interval = window.setInterval(() => {
      setViewers((prev) => {
        const drift = Math.floor(Math.random() * 5) - 2; // -2 .. +2
        const next = prev + drift;
        // Keep it within +/- 4 of the base so it doesn't wander too far
        if (next < baseViewers - 4) return baseViewers - 4;
        if (next > baseViewers + 4) return baseViewers + 4;
        if (next < 3) return 3;
        return next;
      });
    }, 4500);
    return () => window.clearInterval(interval);
  }, [baseViewers]);

  useEffect(() => {
    if (quantity > maxQty) setQuantity(maxQty);
  }, [maxQty, quantity]);

  const showToast = (msg: string) => {
    setToast(msg);
    window.setTimeout(() => setToast(null), 2200);
  };

  const handleAddToCart = () => {
    if (!product || !inStock || !displayed) return;
    addItem({
      productId: product.id,
      productName: product.name,
      image: getProductImage(product),
      skuId: matchedSku?.id ?? null,
      skuLabel: matchedSku?.label ?? null,
      unitPrice: displayed.listPrice,
      finalPrice: displayed.finalPrice,
      quantity,
      maxStock: displayed.stock,
      currency: product.currency ?? "USD",
    });
    showToast("Added to cart");
    openCart();
  };

  const toggleWishlist = () => {
    if (productId == null) return;
    const next = isWished
      ? wishlist.filter((n) => n !== productId)
      : [...wishlist, productId];
    setWishlist(next);
    writeWishlist(next);
    if (!isWished) {
      setHeartBeat(true);
      window.setTimeout(() => setHeartBeat(false), 500);
      showToast("Saved to wishlist");
    } else {
      showToast("Removed from wishlist");
    }
  };

  const handleShare = async () => {
    const shareData = {
      title: product?.name ?? "Hearth & Heirloom",
      text: product?.name ?? "",
      url: window.location.href,
    };
    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(window.location.href);
        showToast("Link copied");
      }
    } catch {
      /* user cancelled */
    }
  };

  if (productId === null) {
    return (
      <main className="min-h-screen bg-background-50 text-foreground-950">
        <Navbar />
        <NotFoundBlock />
        <Footer />
      </main>
    );
  }

  const categoryName = product?.product_categories?.name ?? "Shop";
  const rating = product ? seededRating(product.id) : { rating: 4.8, reviews: 42 };

  // Build details accordion content from product fields + tasteful brand defaults
  const detailItems: DetailItem[] = product
    ? [
        {
          id: "story",
          title: "About this piece",
          icon: "ri-book-open-line",
          body:
            product.description?.trim() ||
            "Hand-finished in our Iowa studio with materials sourced from within a hundred miles. No two pieces are ever exactly alike — that's the point.",
        },
        {
          id: "materials",
          title: "Materials & care",
          icon: "ri-leaf-line",
          body:
            "Natural, undyed fibers and locally sourced hardwoods. Spot-clean with cool water; air dry. Avoid direct sun for extended periods. Hand-wash recommended for textile pieces. Re-condition wood with raw beeswax once a year.",
        },
        {
          id: "dimensions",
          title: "Size & weight",
          icon: "ri-ruler-line",
          body:
            "Slight variation is expected — each piece is hand-finished. See product photos for scale. Reach out via the Contact page for exact measurements if you need them for a fitted space.",
        },
        {
          id: "shipping",
          title: "Shipping & returns",
          icon: "ri-truck-line",
          body:
            "Carefully packed in recycled materials and shipped within 2–3 business days. Free U.S. shipping on orders over $150. 30-day no-fuss returns — if it doesn't feel right at home, send it back.",
        },
        {
          id: "maker",
          title: "Meet the maker",
          icon: "ri-hand-heart-line",
          body:
            "Made by a small studio of three full-time artisans and one shop dog. Every piece is signed on the underside. We'd love to tell you about the maker behind your piece — just reply to your order confirmation email.",
        },
      ]
    : [];

  return (
    <main className="min-h-screen bg-background-50 text-foreground-950">
      <Navbar />

      {/* Breadcrumbs */}
      <div className="pt-28 md:pt-32 px-4 md:px-10 lg:px-14">
        <nav
          aria-label="Breadcrumb"
          className="flex items-center gap-2 text-xs text-foreground-600"
        >
          <Link to="/" className="hover:text-foreground-950 cursor-pointer">Home</Link>
          <i className="ri-arrow-right-s-line text-sm text-foreground-400"></i>
          <Link to="/#collections" className="hover:text-foreground-950 cursor-pointer">
            Shop
          </Link>
          {product && (
            <>
              <i className="ri-arrow-right-s-line text-sm text-foreground-400"></i>
              <span className="text-foreground-700">{categoryName}</span>
            </>
          )}
          {product && (
            <>
              <i className="ri-arrow-right-s-line text-sm text-foreground-400"></i>
              <span className="text-foreground-950 truncate max-w-[200px]">{product.name}</span>
            </>
          )}
        </nav>
      </div>

      {/* Loading state */}
      {loading && (
        <section className="px-4 md:px-10 lg:px-14 py-10 pb-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 md:gap-14">
            <div className="aspect-[4/5] rounded-2xl bg-background-100 animate-pulse" />
            <div className="space-y-4">
              <div className="h-6 w-24 bg-background-100 rounded animate-pulse" />
              <div className="h-10 w-3/4 bg-background-100 rounded animate-pulse" />
              <div className="h-5 w-1/2 bg-background-100 rounded animate-pulse" />
              <div className="h-24 bg-background-100 rounded animate-pulse" />
              <div className="h-12 w-full bg-background-100 rounded animate-pulse" />
            </div>
          </div>
        </section>
      )}

      {/* Error */}
      {!loading && error && (
        <section className="px-4 md:px-10 lg:px-14 py-20">
          <div className="rounded-2xl border border-background-200 bg-background-50 p-8 max-w-xl mx-auto text-center">
            <p className="text-sm text-foreground-700 mb-3">
              We couldn't load this piece. {error}
            </p>
            <button
              type="button"
              onClick={() => window.location.reload()}
              className="inline-flex items-center gap-2 px-5 h-10 rounded-full bg-foreground-950 text-background-50 text-sm cursor-pointer whitespace-nowrap"
            >
              <i className="ri-refresh-line"></i>
              Try again
            </button>
          </div>
        </section>
      )}

      {!loading && !error && !product && <NotFoundBlock />}

      {/* Main detail */}
      {!loading && !error && product && displayed && (
        <>
          <section className="px-4 md:px-10 lg:px-14 pt-6 md:pt-10 pb-16 md:pb-24">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-14 items-start">
              {/* Gallery — desktop is a tall scrollable stack of large images */}
              <div>
                <ProductGallery
                  media={media}
                  alt={product.name}
                  onOpenLightbox={(index) => setLightbox({ open: true, index })}
                />
              </div>

              {/* Info — sticky on desktop so it stays with you while you scroll the gallery */}
              <div className="lg:sticky lg:top-28">
                <Reveal from="up">
                  <p className="text-xs tracking-[0.3em] uppercase text-foreground-600 mb-3">
                    {categoryName}
                  </p>
                  <h1 className="font-heading text-3xl md:text-5xl text-foreground-950 leading-[1.05]">
                    {product.name}
                  </h1>

                  {/* Rating + reviews */}
                  <a
                    href="#reviews"
                    className="mt-4 inline-flex items-center gap-3 text-sm cursor-pointer group"
                  >
                    <span className="flex items-center gap-0.5 text-secondary-600">
                      {Array.from({ length: 5 }).map((_, i) => {
                        const filled = i < Math.round(rating.rating);
                        return (
                          <i
                            key={i}
                            className={`${filled ? "ri-star-fill" : "ri-star-line"} text-[15px]`}
                          ></i>
                        );
                      })}
                    </span>
                    <span className="text-foreground-700 group-hover:text-foreground-950 underline-offset-4 group-hover:underline">
                      {rating.rating.toFixed(1)} · {rating.reviews} reviews
                    </span>
                  </a>

                  {/* Price */}
                  <div className="mt-6 flex items-baseline gap-3">
                    <span className="font-heading text-3xl md:text-4xl text-foreground-950">
                      ${displayed.finalPrice.toFixed(2)}
                    </span>
                    {displayed.hasDiscount && (
                      <>
                        <span className="text-base text-foreground-500 line-through">
                          ${displayed.listPrice.toFixed(2)}
                        </span>
                        <span className="inline-flex items-center px-2 h-6 rounded-full bg-primary-100 text-primary-700 text-[11px] tracking-widest uppercase">
                          Save ${(displayed.listPrice - displayed.finalPrice).toFixed(0)}
                        </span>
                      </>
                    )}
                  </div>

                  {/* Stock indicator */}
                  <div className="mt-4 flex items-center gap-2 text-sm">
                    <span
                      className={`w-2 h-2 rounded-full ${
                        !inStock
                          ? "bg-foreground-400"
                          : lowStock
                            ? "bg-secondary-500 animate-pulse"
                            : "bg-accent-500"
                      }`}
                    ></span>
                    <span className="text-foreground-700">
                      {!inStock
                        ? "Currently sold out — restock in 2–3 weeks"
                        : lowStock
                          ? `Only ${displayed.stock} left — almost gone`
                          : "In stock and ready to ship"}
                    </span>
                  </div>
                </Reveal>

                {/* Variants */}
                {variants.length > 0 && (
                  <div className="mt-8">
                    <VariantPicker
                      variants={variants}
                      selected={selectedOptions}
                      onSelect={(name, opt) =>
                        setSelectedOptions((s) => ({ ...s, [name]: opt }))
                      }
                    />
                  </div>
                )}

                {/* Live social proof — viewers right now */}
                <div className="mt-7 flex items-center gap-2 text-xs text-foreground-700">
                  <span className="relative flex w-2 h-2">
                    <span className="absolute inset-0 rounded-full bg-accent-500/60 animate-ping"></span>
                    <span className="relative w-2 h-2 rounded-full bg-accent-500"></span>
                  </span>
                  <span>
                    <span className="font-medium text-foreground-950 tabular-nums">{viewers}</span>{" "}
                    {viewers === 1 ? "person is" : "people are"} viewing this right now
                  </span>
                </div>

                {/* Qty + Add to cart + Save + Share — all in one row */}
                <div className="mt-3 flex items-stretch gap-2">
                  <QuantityStepper
                    value={quantity}
                    onChange={setQuantity}
                    max={maxQty}
                  />
                  <button
                    type="button"
                    onClick={handleAddToCart}
                    disabled={!inStock}
                    className="flex-1 min-w-0 inline-flex items-center justify-center gap-2 px-4 sm:px-6 h-12 rounded-full bg-foreground-950 hover:bg-foreground-800 text-background-50 text-sm font-medium transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <i className="ri-shopping-bag-line shrink-0"></i>
                    <span className="truncate">
                      {inStock
                        ? `Add to cart — $${(displayed.finalPrice * quantity).toFixed(2)}`
                        : "Sold out"}
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={toggleWishlist}
                    aria-label={isWished ? "Remove from wishlist" : "Save for later"}
                    title={isWished ? "Saved" : "Save for later"}
                    className={`w-12 h-12 shrink-0 flex items-center justify-center rounded-full border transition-colors cursor-pointer ${
                      isWished
                        ? "border-primary-500 bg-primary-50 text-primary-700"
                        : "border-background-300 text-foreground-800 hover:border-foreground-500 hover:text-foreground-950"
                    }`}
                  >
                    <i
                      className={`${isWished ? "ri-heart-fill" : "ri-heart-line"} text-lg ${
                        heartBeat ? "animate-heartPop" : ""
                      }`}
                    ></i>
                  </button>
                  <button
                    type="button"
                    onClick={handleShare}
                    aria-label="Share"
                    title="Share"
                    className="w-12 h-12 shrink-0 flex items-center justify-center rounded-full border border-background-300 text-foreground-800 hover:border-foreground-500 hover:text-foreground-950 cursor-pointer transition-colors"
                  >
                    <i className="ri-share-line text-lg"></i>
                  </button>
                </div>

                {/* Trust badges */}
                <ul className="mt-8 grid grid-cols-2 gap-3 pt-6 border-t border-background-200">
                  {[
                    { icon: "ri-hand-heart-line", title: "Handmade in Iowa", body: "Studio-signed" },
                    { icon: "ri-truck-line", title: "Free over $150", body: "Ships in 2–3 days" },
                    { icon: "ri-arrow-go-back-line", title: "30-day returns", body: "No fuss, no fee" },
                    { icon: "ri-leaf-line", title: "Natural materials", body: "Locally sourced" },
                  ].map((b) => (
                    <li
                      key={b.title}
                      className="flex items-start gap-2.5 p-3 rounded-lg bg-background-100/60 hover:bg-background-100 transition-colors"
                    >
                      <span className="w-8 h-8 shrink-0 flex items-center justify-center rounded-full bg-background-50 text-foreground-950">
                        <i className={`${b.icon} text-base`}></i>
                      </span>
                      <span className="flex flex-col leading-tight">
                        <span className="text-xs font-medium text-foreground-950">{b.title}</span>
                        <span className="text-[11px] text-foreground-600">{b.body}</span>
                      </span>
                    </li>
                  ))}
                </ul>

                {/* Details accordion */}
                <div className="mt-10">
                  <DetailsAccordion items={detailItems} />
                </div>
              </div>
            </div>
          </section>

          {/* Promise band */}
          <section className="border-y border-background-200 bg-background-100/50 overflow-hidden">
            <div className="flex animate-marquee whitespace-nowrap py-4 text-foreground-700 text-sm">
              {Array.from({ length: 2 }).map((_, dup) => (
                <div key={dup} className="flex items-center gap-10 px-5 shrink-0">
                  {[
                    { icon: "ri-flower-line", text: "Made by hand, not by factory" },
                    { icon: "ri-recycle-line", text: "Natural & locally sourced materials" },
                    { icon: "ri-time-line", text: "Built to outlast trends" },
                    { icon: "ri-customer-service-2-line", text: "Personal support from a real human" },
                    { icon: "ri-leaf-line", text: "Carbon-neutral shipping" },
                  ].map((m, i) => (
                    <span key={`${dup}-${i}`} className="flex items-center gap-2.5">
                      <i className={`${m.icon} text-base text-foreground-950`}></i>
                      {m.text}
                    </span>
                  ))}
                </div>
              ))}
            </div>
          </section>

          {/* Reviews + Q&A + Shipping tabs */}
          <ReviewsSection
            productId={product.id}
            productName={product.name}
            rating={rating.rating}
            reviewCount={rating.reviews}
          />

          {/* Related */}
          {related.length > 0 && (
            <section className="px-4 md:px-10 lg:px-14 py-20 md:py-28">
              <Reveal from="up" className="flex items-end justify-between mb-8 md:mb-10">
                <h2 className="font-heading text-2xl md:text-4xl text-foreground-950">
                  Pieces that go with this one
                </h2>
                <Link
                  to="/#collections"
                  className="hidden sm:inline-flex items-center gap-1.5 text-sm text-foreground-700 hover:text-foreground-950 cursor-pointer whitespace-nowrap group"
                >
                  Browse the full shop
                  <i className="ri-arrow-right-line transition-transform group-hover:translate-x-1"></i>
                </Link>
              </Reveal>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
                {related.map((p, i) => (
                  <Reveal key={p.id} from="up" delay={i * 80}>
                    <ProductCard product={p} />
                  </Reveal>
                ))}
              </div>
            </section>
          )}
        </>
      )}

      <Footer />

      {/* Lightbox */}
      {product && (
        <Lightbox
          open={lightbox.open}
          media={media}
          index={lightbox.index}
          alt={product.name}
          onClose={() => setLightbox((s) => ({ ...s, open: false }))}
          onChange={(i) => setLightbox({ open: true, index: i })}
        />
      )}

      {/* Toast */}
      {toast && (
        <div
          className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 px-5 py-3 rounded-full bg-foreground-950 text-background-50 text-sm flex items-center gap-2 shadow"
          role="status"
        >
          <i className="ri-check-line"></i>
          {toast}
        </div>
      )}
    </main>
  );
}

function NotFoundBlock() {
  return (
    <section className="px-4 md:px-10 lg:px-14 py-32">
      <div className="max-w-xl mx-auto text-center">
        <h2 className="font-heading text-2xl md:text-3xl text-foreground-950">
          Product not found
        </h2>
        <Link
          to="/#collections"
          className="mt-5 inline-flex items-center gap-2 px-5 h-10 rounded-full bg-foreground-950 text-background-50 text-sm font-medium cursor-pointer whitespace-nowrap"
        >
          Back to shop
        </Link>
      </div>
    </section>
  );
}